import {
  Alert,
  Button,
  ButtonGroup,
  Dropdown,
  DropdownButton,
  Row,
} from 'react-bootstrap';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Styled from 'styled-components';
import { orderBy, isEqual } from 'lodash';
import {
  confirmReport,
  getMultiPdf,
  getReportList,
  getXlsxReport,
} from '../../utils/API/reports';
import { UserContext } from '../../context/UserContext';
import { MonthNavHeader } from '../../components/Layout/MonthNavHeader';
import I3Modal from '../../components/Layout/I3Modal';
import MailForm from '../../components/MailForm';
import RowDetail from '../../components/PanelTotal/rowDetailPanelTotal';
import { renderSpinner } from '../../utils/Utility';
import SendFiscalDocumentsForm from './sendFiscalDocumentsForm';
import { MonthContext } from '../../context/MonthContext';
import { success, error } from '../../utils/notification';
import { Unauthorized } from '../../components/Unauthorized';
import { PERMISSIONS, API } from '../../constants';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { getCompanies } from '../../utils/API/companies';
import { sendEmail } from '../../utils/API/user';
import AdministrationDataGrid from './AdministrationDataGrid';

const Hr = Styled.hr`
  border-top: 1px solid var(--brand);
`;

const fetchData = async (company, yyyy, mm) => {
  try {
    const reportsData = await getReportList(company, yyyy, mm);
    return orderBy(reportsData, ['all_confirmed', 'last_name'], ['asc']);
  } catch (e) {
    return undefined;
  }
};

const CardRow = Styled(Row)`
    padding: 7px 15px;
`;

const POLLING_INTERVAL_MS = 60_000;

const Administration = () => {
  const { hasClaim } = useContext(UserContext);
  const { month } = useContext(MonthContext);

  const [isLoading, setIsLoading] = useState(true);
  const [reports, setReports] = useState([]);
  // Dati ricaricati dal polling ma non ancora applicati alla griglia:
  // l'utente li applica dal banner quando ha finito di lavorare.
  const [pendingReports, setPendingReports] = useState(null);
  // Ref ai report mostrati, per confrontarli nel polling senza
  // ricreare di continuo l'intervallo.
  const reportsRef = useRef(reports);
  const [showModal, setShowModal] = useState(false);
  const [showFiscalModal, setShowFiscalModal] = useState(false);
  const [selectedFlatRows, setSelectedFlatRows] = useState([]);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(1);

  const getSelectedRows = () => selectedFlatRows.map((r) => r.original);

  // Filter reports: solo employees (anche i licenziati devono avere il ruolo employees)
  // con outgoing_date null o >= primo giorno del mese selezionato
  const filteredReports = useMemo(
    () =>
      reports.filter((report) => {
        // Defense-in-depth: anche se il backend filtra per ruolo employees,
        // se Keycloak fallisce e torna tutti, qui escludiamo i non-employees
        if (!report.is_employee) {
          return false;
        }
        // Attivi
        if (!report.outgoing_date) {
          return true;
        }
        // Licenziati: include solo se outgoing_date >= primo del mese
        const outgoingDate = new Date(report.outgoing_date);
        const firstDayOfMonth = month.clone().startOf('month').toDate();
        return outgoingDate >= firstDayOfMonth;
      }),
    [reports, month]
  );

  const handleHideMailModal = () => setShowModal(false);
  const handleHideFiscalModal = () => setShowFiscalModal(false);

  const monthMM = month.format('MM');
  const yearYYYY = month.format('YYYY');
  const filenameWithDate = useMemo(
    () => (name, ext) => `${yearYYYY}-${monthMM} - ${name}.${ext}`,
    [yearYYYY, monthMM]
  );

  // Mantiene il ref allineato ai report mostrati.
  useEffect(() => {
    reportsRef.current = reports;
  }, [reports]);

  const updateData = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setIsLoading(true);
      const response = await fetchData(selectedCompany, yearYYYY, monthMM);
      if (response) {
        if (silent) {
          // Polling: non disturbare la griglia, segnala solo se i dati
          // sono cambiati rispetto a quelli mostrati.
          setPendingReports(
            isEqual(reportsRef.current, response) ? null : response
          );
        } else {
          // Caricamento esplicito: applica subito e azzera il banner.
          setReports(response);
          setPendingReports(null);
        }
      }
      if (!silent) setIsLoading(false);
    },
    [selectedCompany, monthMM, yearYYYY]
  );

  const applyPendingData = useCallback(() => {
    setPendingReports((pending) => {
      if (pending) setReports(pending);
      return null;
    });
  }, []);

  const generatePDF = useCallback(
    async (users, types, filename, toBePrint = false) => {
      setIsLoading(true);
      try {
        const res = await getMultiPdf({
          data: {
            usernames: users || [],
            types,
            to_be_print: toBePrint,
          },
          company: selectedCompany,
          year: yearYYYY,
          month: monthMM,
        });
        const url = window.URL.createObjectURL(res.data);
        const tempLink = document.createElement('a');
        tempLink.href = url;
        tempLink.target = '_blank';
        tempLink.setAttribute('download', filenameWithDate(filename, 'pdf'));
        tempLink.click();
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
      }
    },
    [selectedCompany, yearYYYY, monthMM, filenameWithDate]
  );

  const generateXLSX = useCallback(
    async (filename, apiUrl) => {
      setIsLoading(true);
      try {
        const res = await getXlsxReport({
          apiUrl,
          company: selectedCompany,
          year: yearYYYY,
          month: monthMM,
        });
        if (res.status === 204) {
          success('Esito', 'Il report XLSX non contiene dati');
          setIsLoading(false);
        } else {
          const url = window.URL.createObjectURL(res.data);
          const tempLink = document.createElement('a');
          tempLink.href = url;
          tempLink.target = '_blank';
          tempLink.setAttribute('download', filenameWithDate(filename, 'xlsx'));
          tempLink.click();
          setIsLoading(false);
        }
      } catch (e) {
        setIsLoading(false);
      }
    },
    [selectedCompany, yearYYYY, monthMM, filenameWithDate]
  );

  // Caricamento iniziale e ricarica esplicita (cambio azienda/mese): mostra lo spinner
  useEffect(() => {
    updateData().catch(() =>
      error('Errore', 'Errore nel caricamento dei dati')
    );
  }, [updateData]);

  // L'utente sta lavorando se ha righe selezionate o un modale aperto:
  // in questi casi il polling non deve aggiornare i dati sotto di lui.
  const isUserBusy = selectedFlatRows.length > 0 || showModal || showFiscalModal;

  // Polling in background: ricarica i dati in modo silenzioso, senza spinner
  // bloccante. In pausa mentre l'utente sta lavorando.
  useEffect(() => {
    const timeoutId = setInterval(() => {
      if (isUserBusy) {
        return;
      }
      updateData({ silent: true }).catch(() => {});
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(timeoutId);
  }, [isUserBusy, updateData]);

  useEffect(() => {
    const getData = async () => {
      setCompanies(await getCompanies());
    };
    getData();
  }, []);

  const lockReports = (lock) =>
    confirmReport(
      {
        confirm: lock,
        notes: '',
        usernames: getSelectedRows().map((r) => r.username),
      },
      month
    ).then(() => updateData());

  return hasClaim(PERMISSIONS.ADMINISTRATION_VIEW) ? (
    <>
      {renderSpinner(isLoading)}
      <div className="content__wrapper">
        <MonthNavHeader title="Amministrazione">
          {companies.length > 0 && (
            <TextField
              sx={{ width: 200 }}
              select
              fullWidth
              size="small"
              label="Azienda"
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            >
              {companies.map((i) => (
                <MenuItem key={i.id} value={i.id}>
                  {i.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </MonthNavHeader>
        <div className="content__fixed_height">
          <div
            className="tabsContainer"
            style={{
              flex: '1 1 0',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {pendingReports && (
              <Alert
                variant="warning"
                className="d-flex align-items-center justify-content-between py-1 px-3 mb-2 small"
              >
                <span>Sono disponibili dati aggiornati.</span>
                <Button
                  variant="primary"
                  size="sm"
                  className="py-0 lh-1"
                  onClick={applyPendingData}
                >
                  Aggiorna
                </Button>
              </Alert>
            )}
            <AdministrationDataGrid
              data={filteredReports}
              generatePDF={generatePDF}
              setSelectedRows={setSelectedFlatRows}
            />
          </div>
        </div>
      </div>
      <div className="sidebar">
        <div className="sidebar__header position-relative">
          Riepilogo {month.format('MMMM YYYY')}
        </div>
        <div className="sidebar__body">
          <RowDetail label="Gli utenti che non hanno chiuso il mese non verranno inclusi" />
          <CardRow>
            <DropdownButton
              as={ButtonGroup}
              variant="primary"
              title="Scarica documenti"
              className="p-0"
            >
              <Dropdown.Item
                onClick={() => {
                  generatePDF([], ['worklogs'], 'Presenze');
                  generateXLSX('Foglio presenze', API.REPORTS_ATTENDANCE_SHEET);
                }}
              >
                Presenze (PDF/XLSX)
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() =>
                  generateXLSX('Incongruenze', API.REPORTS_ABSENCES_DISC)
                }
              >
                Incongruenze (XLSX)
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() =>
                  generateXLSX(
                    'Ticket Restaurant',
                    API.REPORTS_TICKET_RESTAURANT
                  )
                }
              >
                Ticket Restaurant (XLSX)
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() =>
                  generatePDF([], ['expenses', 'trips'], 'Nota spese')
                }
              >
                Nota spese (PDF)
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() =>
                  generateXLSX('Riepilogo nota spese', API.REPORTS_XLSX)
                }
              >
                Riepilogo nota spese (XLSX)
              </Dropdown.Item>
            </DropdownButton>
          </CardRow>
          <Hr />
          <CardRow>
            <DropdownButton
              as={ButtonGroup}
              variant="primary"
              title="Cambia stato mese"
              className="p-0"
              disabled={getSelectedRows().length === 0}
            >
              <Dropdown.Item onClick={() => lockReports(false)}>
                Apri
              </Dropdown.Item>
              <Dropdown.Item onClick={() => lockReports(true)}>
                Chiudi
              </Dropdown.Item>
            </DropdownButton>
          </CardRow>
          <CardRow>
            <Button
              variant="primary"
              onClick={() => setShowModal(true)}
              disabled={getSelectedRows().length === 0}
            >
              Contatta
            </Button>
          </CardRow>
          <Hr />
          <CardRow>
            <Button variant="primary" onClick={() => setShowFiscalModal(true)}>
              Invia documenti fiscali
            </Button>
          </CardRow>
        </div>
      </div>
      <I3Modal
        show={showModal}
        onHide={handleHideMailModal}
        title="Contatta"
        className=""
      >
        <MailForm
          htmlEditor={true}
          users={getSelectedRows()}
          onSend={sendEmail}
          onClose={handleHideMailModal}
        />
      </I3Modal>
      <I3Modal
        show={showFiscalModal}
        onHide={handleHideFiscalModal}
        title="Invia documenti fiscali via mail"
        className=""
      >
        <SendFiscalDocumentsForm
          companyId={selectedCompany}
          close={handleHideFiscalModal}
        />
      </I3Modal>
    </>
  ) : (
    <Unauthorized />
  );
};

export default Administration;
